import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, Flame, Clock, TrendingUp } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { CommentTree } from "./CommentItem";
import { MediaUploader, MediaPreview } from "@/components/MediaUploader";

export function CommentsSection({ targetType, targetId }) {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [sort, setSort] = useState("top");
    const [loading, setLoading] = useState(true);
    const [body, setBody] = useState("");
    const [posting, setPosting] = useState(false);
    const [attachment, setAttachment] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/social/comments", { params: { target_type: targetType, target_id: targetId, sort } });
            setItems(data.items || []);
        } catch (e) { /* ignore */ }
        finally { setLoading(false); }
    }, [targetType, targetId, sort]);

    useEffect(() => { if (targetId) load(); }, [load, targetId]);

    async function postTopLevel() {
        if (!body.trim() && !attachment) return;
        setPosting(true);
        try {
            const payload = { target_type: targetType, target_id: targetId, body: body.trim() || "(attached media)" };
            if (attachment) payload.media_id = attachment.id;
            const { data } = await api.post("/social/comments", payload);
            setItems((prev) => [data, ...prev]);
            setBody("");
            setAttachment(null);
        } catch (e) { /* ignore */ }
        finally { setPosting(false); }
    }

    const onReply = (newC) => setItems((prev) => [...prev, newC]);
    const onUpdated = (upd) => setItems((prev) => prev.map((c) => (c.id === upd.id ? { ...c, ...upd } : c)));

    return (
        <section className="mt-12" data-testid="comments-section">
            <div className="flex items-center justify-between mb-5">
                <h3 className="font-display text-xl flex items-center gap-2">
                    <MessageCircle className="w-4 h-4 opacity-70" />
                    Witness Discussion
                    <span className="font-mono text-xs opacity-50 ml-2">{items.filter((c) => !c.deleted_at).length}</span>
                </h3>
                <div className="flex items-center gap-1 text-xs">
                    {[
                        { k: "top", label: "Top", icon: TrendingUp },
                        { k: "new", label: "New", icon: Clock },
                        { k: "controversial", label: "Controversial", icon: Flame },
                    ].map(({ k, label, icon: Icon }) => (
                        <button
                            key={k}
                            onClick={() => setSort(k)}
                            className={`px-2.5 py-1 rounded font-mono uppercase tracking-wide flex items-center gap-1 transition ${sort === k ? "bg-[var(--ok-accent)] text-black" : "opacity-60 hover:opacity-100"}`}
                            data-testid={`sort-${k}`}
                        >
                            <Icon className="w-3 h-3" /> {label}
                        </button>
                    ))}
                </div>
            </div>

            {user ? (
                <div className="ok-card p-4 mb-6" data-testid="comment-compose">
                    <textarea
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                        rows={3}
                        placeholder="Share your analysis, witness account, or research note…"
                        className="ok-input w-full text-sm"
                        data-testid="comment-textarea"
                    />
                    {attachment && (
                        <div className="mt-3" data-testid="comment-attachment-preview">
                            <MediaPreview media={attachment} onRemove={() => setAttachment(null)} />
                        </div>
                    )}
                    <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
                        <div className="text-xs opacity-50 font-mono">Posting as <Link to={`/u/${user.id}`} className="text-[var(--ok-accent)] hover:underline">{user.name}</Link></div>
                        <div className="flex items-center gap-2">
                            <MediaUploader compact onUploaded={setAttachment} testId="comment-attach" />
                            <button onClick={postTopLevel} disabled={posting || (!body.trim() && !attachment)} className="ok-btn-primary text-sm" data-testid="comment-submit">
                                {posting ? "Posting…" : "Post comment"}
                            </button>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="ok-card p-5 mb-6 text-center" data-testid="comment-login-cta">
                    <p className="text-sm opacity-80 mb-3">Sign in to discuss this dossier and vote on the archive.</p>
                    <Link to="/login" className="ok-btn-primary text-sm inline-flex">Log in</Link>
                </div>
            )}

            {loading ? (
                <div className="text-xs font-mono opacity-50">Loading discussion…</div>
            ) : items.filter((c) => !c.deleted_at).length === 0 ? (
                <div className="ok-card p-6 text-center text-sm opacity-70" data-testid="comments-empty">
                    No discussion yet. Be the first to weigh in.
                </div>
            ) : (
                <CommentTree items={items} targetType={targetType} targetId={targetId} onReply={onReply} onUpdated={onUpdated} />
            )}
        </section>
    );
}
